import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setEmailFormHint } from '@store/slices/form-hints.slice';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { RxUtils } from '@utils';
import { HttpError, HttpStatusCode, authService } from '@services';
import { Box, Stack, Typography } from '@mui/material';
import { AuthLayout, FormikSubmitButton, FormikTextfield, RouterLinkAnchor } from '@components/common';
import { SadFaceEmojiIcon } from '@components/icons';

type FormFields = Extendable<{
	email: string;
}>;

export const ForgotPassword: FC = () => {
	const { t } = useTranslation('common');
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const formik = useFormik<FormFields>({
		initialValues: { email: '' },
		validateOnChange: true,
		validationSchema: yup.object({
			email: yup.string().email(t('validation.email-invalid')).required(t('validation.email-required'))
		}),
		onSubmit: ({ email }, api) => {
			RxUtils.promisify(
				authService.forgotPassword(email),
				() => navigate(`/reset-password?email=${email}`),
				(err) => {
					if (err instanceof HttpError && err.statusCode === HttpStatusCode.PRECONDITION_REQUIRED) {
						navigate(`/verify-user/${email}`);
					} else {
						return undefined;
					}
				},
				() => api.setSubmitting(false)
			);
		}
	});

	return (
		<AuthLayout>
			<Box component="form" onSubmit={formik.handleSubmit} sx={{ flexGrow: 1 }}>
				<Stack direction="column" alignItems="center" justifyContent="center" spacing={10} flexGrow={1}>
					<Stack direction="column" alignItems="center">
						<SadFaceEmojiIcon sx={{ width: 48, height: 48 }} />
						<Typography variant="h2" sx={{ marginTop: 4 }}>
							{t('forgot-password.title')}
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ marginTop: 2 }}>
							<Typography>{t('forgot-password.caption')}</Typography>
						</Stack>
					</Stack>
					<Box>
						<FormikTextfield
							formikApi={formik}
							name="email"
							title={t('email')}
							placeholder={t('email')}
							sx={{ minWidth: 300, maxWidth: 300 }}
							onChange={(e) => dispatch(setEmailFormHint(e.target.value))}
						/>
					</Box>

					<Stack direction="column" alignItems="center" spacing={5}>
						<FormikSubmitButton
							color="primary"
							formik={formik}
							data-testid="send-forget-password-instructions-form-submit-button"
						>
							{t('forgot-password.send-instructions-button')}
						</FormikSubmitButton>
						<RouterLinkAnchor href="/login" data-testid="forget-password-login-link">
							<Typography variant="body1">{t('forgot-password.login-link')}</Typography>
						</RouterLinkAnchor>
					</Stack>
				</Stack>
			</Box>
		</AuthLayout>
	);
};
